/**
 * useTodoToggleMutation — Mutation hook for toggling todo completion status.
 * Uses optimistic updates: the UI flips immediately and is rolled back on error.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuth } from "@/hooks/useAuth";
import { useCallback } from "react";

export const useTodoToggleMutation = () => {
  const { handleError } = useErrorHandler();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const toggleTodoMutation = useMutation({
    mutationFn: async ({ todoId }: { goalTitle?: string, todoId: string }) => {
      if (!user) throw new Error("User must be authenticated");

      // Read current state so we can compute the toggle on the server
      const { data: todo } = await supabase
        .from('todos')
        .select('completed')
        .eq('id', todoId)
        .single();

      if (!todo) throw new Error("Todo not found");

      const { data, error } = await supabase
        .from('todos')
        .update({ completed: !todo.completed })
        .eq('id', todoId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ todoId }) => {
      // Cancel in-flight refetches so they don't overwrite the optimistic update
      await queryClient.cancelQueries({ queryKey: ["goals"] });

      const previousGoals = queryClient.getQueryData<any>(["goals"]);

      // Walk the cached goals tree and flip the matching todo's `completed` flag
      queryClient.setQueriesData({ queryKey: ["goals"] }, (old: any) => {
        if (!old) return old;
        const flip = (goals: any[]) => goals.map((g) => ({
          ...g,
          todos: (g.todos || []).map((t: any) =>
            t.id === todoId ? { ...t, completed: !t.completed } : t
          ),
        }));
        return Array.isArray(old) ? flip(old) : old;
      });

      return { previousGoals };
    },
    onError: (error: Error, _vars, context) => {
      // Rollback on failure
      if (context?.previousGoals) {
        queryClient.setQueryData(["goals"], context.previousGoals);
      }
      handleError(error, { title: "Error toggling todo" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
    },
  });

  const toggleTodo = useCallback((params: { goalTitle?: string, todoId: string }) => {
    return toggleTodoMutation.mutate(params);
  }, [toggleTodoMutation]);

  return { toggleTodo };
};
